/**
 * Cart upsell discount codes — apply only when added from cart upsell, not from PDP.
 * Preserves other active discount codes (e.g. WELKOM10).
 */
(function () {
  let pendingApplyCode = null;
  let pendingRemoveCode = null;
  let submitListenerInstalled = false;

  function normalizeCode(code) {
    return (code || "").trim();
  }

  function codesMatch(a, b) {
    return normalizeCode(a).toUpperCase() === normalizeCode(b).toUpperCase();
  }

  async function fetchCart() {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart.js`, { credentials: "same-origin" });
    if (!response.ok) throw new Error(response.status);
    return response.json();
  }

  function extractCartDiscountCodes(cart) {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes
        .map((entry) => {
          if (typeof entry === "string") return normalizeCode(entry);
          return normalizeCode(entry.code || entry.title);
        })
        .filter(Boolean);
    }

    return (cart.cart_level_discount_applications || [])
      .map((app) => normalizeCode(app.title))
      .filter(Boolean);
  }

  async function setCartDiscountCodes(codes) {
    const unique = [];
    const seen = new Set();

    codes.forEach((code) => {
      const normalized = normalizeCode(code);
      const key = normalized.toUpperCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      unique.push(normalized);
    });

    const root = window.Shopify?.routes?.root ?? "/";
    await fetch(`${root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: unique.join(",") }),
    });
  }

  async function addDiscountCode(code) {
    const normalized = normalizeCode(code);
    if (!normalized) return false;

    const cart = await fetchCart();
    const current = extractCartDiscountCodes(cart);
    if (current.some((existing) => codesMatch(existing, normalized))) {
      return false;
    }

    await setCartDiscountCodes([...current, normalized]);
    return true;
  }

  async function removeDiscountCodes(codesToRemove) {
    const removeSet = new Set(
      (Array.isArray(codesToRemove) ? codesToRemove : [codesToRemove])
        .map((code) => normalizeCode(code).toUpperCase())
        .filter(Boolean),
    );
    if (!removeSet.size) return false;

    const cart = await fetchCart();
    const current = extractCartDiscountCodes(cart);
    const remaining = current.filter(
      (code) => !removeSet.has(normalizeCode(code).toUpperCase()),
    );

    if (remaining.length === current.length) return false;

    await setCartDiscountCodes(remaining);
    return true;
  }

  async function fetchCartSections(cart) {
    if (!cart?.getSectionsToRender) return null;

    const root = window.Shopify?.routes?.root ?? "/";
    const sectionIds = cart
      .getSectionsToRender()
      .map((section) => section.id)
      .join(",");

    const response = await fetch(`${root}?sections=${sectionIds}`);
    if (!response.ok) return null;

    return { sections: await response.json() };
  }

  async function refreshCartDrawer() {
    const cart = document.querySelector("cart-drawer");
    if (!cart) return;

    if (typeof cart.refreshCartDrawer === "function") {
      cart.refreshCartDrawer({});
      return;
    }

    if (typeof cart.renderContents === "function") {
      const refreshed = await fetchCartSections(cart);
      if (refreshed) cart.renderContents(refreshed);
    }
  }

  function installSubmitListener() {
    if (submitListenerInstalled) return;
    submitListenerInstalled = true;

    document.addEventListener(
      "submit",
      (event) => {
        const upsellForm = event.target.closest?.(
          ".oo-cart-upsell-item__product-form",
        );
        if (upsellForm) {
          const productForm = upsellForm.closest("product-form");
          pendingApplyCode = normalizeCode(
            productForm?.dataset.ooUpsellDiscountCode,
          );
          pendingRemoveCode = null;
          return;
        }

        const form = event.target.closest?.('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const productForm = form.closest("product-form");
        if (!productForm) return;

        if (productForm.dataset.ooAutoDiscountCodes) {
          pendingApplyCode = null;
          pendingRemoveCode = null;
          return;
        }

        const upsellCode = normalizeCode(productForm.dataset.ooUpsellDiscountCode);
        if (upsellCode) {
          pendingApplyCode = null;
          pendingRemoveCode = upsellCode;
        }
      },
      true,
    );
  }

  function installCartUpdateHook() {
    if (window.__ooCartUpsellDiscountHook) return;
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      return;
    }

    window.__ooCartUpsellDiscountHook = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== "product-form") return;

      const applyCode = pendingApplyCode;
      const removeCode = pendingRemoveCode;

      pendingApplyCode = null;
      pendingRemoveCode = null;

      if (!applyCode && !removeCode) return;

      (async () => {
        try {
          let changed = false;

          if (applyCode) {
            changed = await addDiscountCode(applyCode);
          } else if (removeCode) {
            changed = await removeDiscountCodes(removeCode);
          }

          if (changed) {
            await refreshCartDrawer();
          }
        } catch (e) {
          console.warn("[oo-cart-upsell-discount]", e);
        }
      })();
    });
  }

  installSubmitListener();
  installCartUpdateHook();
})();
